import React from 'react';
import { useDashboardLocaleStore } from '@/presentation/dashboard/useDashboardLocaleStore';
import { normalizeLocale } from '@/i18n/localeCatalog';

function L(locale, vi, en, zh) {
  const id = normalizeLocale(locale);
  if (id === 'en') return en;
  if (id === 'zh') return zh;
  return vi;
}

function pickRows(locale, viRows, enRows, zhRows) {
  const id = normalizeLocale(locale);
  if (id === 'en') return enRows;
  if (id === 'zh') return zhRows;
  return viRows;
}

export function normalizeDashboardHomeRole(role) {
  if (role === 'admin' || role === 'super_admin') return 'admin';
  if (role === 'teacher') return 'teacher';
  if (role === 'parent') return 'parent';
  return 'student';
}

export function getPublicSpotlightStrings(locale) {
  return {
    loading: L(locale, 'Đang tải danh sách bài test...', 'Loading public quizzes…', '正在加载公开测验列表…'),
    empty: L(locale, 'Chưa có bài test công khai.', 'No public quizzes yet.', '暂无公开测验。'),
    descFallback: L(
      locale,
      'Bài luyện ngữ pháp IELTS công khai.',
      'Public IELTS grammar practice.',
      '公开 IELTS 语法练习。',
    ),
    thumbAlt: L(locale, 'Ảnh đại diện (mẫu)', 'Thumbnail placeholder', '缩略图占位'),
  };
}

export function getGuestBannerStrings(locale) {
  return {
    title: L(locale, 'Bạn đang dùng chế độ Guest', 'You are in Guest mode', '您正在使用访客模式'),
    body: L(
      locale,
      'Nên nâng cấp tài khoản để đồng bộ dữ liệu giữa nhiều thiết bị và không bị mất khi xóa cache.',
      'Create an account to sync across devices and avoid losing data when the cache is cleared.',
      '建议注册账户以在多设备同步数据，并避免清除缓存后丢失。',
    ),
    register: L(locale, 'Tạo tài khoản', 'Create account', '创建账户'),
    login: L(locale, 'Đăng nhập', 'Log in', '登录'),
  };
}

export function getDashboardHomeCopy(role, locale) {
  const r = normalizeDashboardHomeRole(role);

  if (r === 'teacher') {
    const demoRows = pickRows(
      locale,
      [
        ['Advanced Architecture I', '142 học sinh', '88%', 'Hoàn thành'],
        ['Renaissance Art History', '98 học sinh', '72%', 'Đang mở'],
        ['Molecular Biology Quiz', '215 học sinh', '92%', 'Hoàn thành'],
      ],
      [
        ['Advanced Architecture I', '142 students', '88%', 'Completed'],
        ['Renaissance Art History', '98 students', '72%', 'Live'],
        ['Molecular Biology Quiz', '215 students', '92%', 'Completed'],
      ],
      [
        ['高等建筑学 I', '142 名学生', '88%', '已完成'],
        ['文艺复兴艺术史', '98 名学生', '72%', '进行中'],
        ['分子生物学测验', '215 名学生', '92%', '已完成'],
      ],
    );
    return {
      heroTitle: L(locale, 'Bảng điều khiển giáo viên', 'Teacher dashboard', '教师工作台'),
      heroSubtitle: L(
        locale,
        'Quản lý quiz, học sinh và theo dõi kết quả lớp học.',
        'Manage quizzes, students, and class performance.',
        '管理测验、学生并跟踪班级表现。',
      ),
      createQuiz: L(locale, '+ Tạo quiz mới', '+ Create new quiz', '+ 新建测验'),
      activeQuizzes: L(locale, 'Quiz đang mở', 'Active quizzes', '进行中的测验'),
      totalParticipants: L(locale, 'Tổng người tham gia', 'Total participants', '总参与人数'),
      avgPerf: L(locale, 'Hiệu suất trung bình', 'Average performance', '平均表现'),
      recentTitle: L(locale, 'Hiệu suất quiz gần đây', 'Recent quiz performance', '近期测验表现'),
      demoRows,
    };
  }

  if (r === 'parent') {
    const completed = pickRows(
      locale,
      [
        ['Tiếng Việt: Cảm thụ văn học', 'Minh Anh', '9.8'],
        ['Khoa học: Chu trình nước', 'Bảo Ngọc', '8.5'],
        ['Toán: Phân số nâng cao', 'Minh Anh', '9.1'],
      ],
      [
        ['Literature: Appreciation', 'Minh Anh', '9.8'],
        ['Science: Water cycle', 'Bảo Ngọc', '8.5'],
        ['Math: Advanced fractions', 'Minh Anh', '9.1'],
      ],
      [
        ['语文：文学欣赏', 'Minh Anh', '9.8'],
        ['科学：水循环', 'Bảo Ngọc', '8.5'],
        ['数学：进阶分数', 'Minh Anh', '9.1'],
      ],
    );
    return {
      heroGreeting: (name) =>
        L(locale, `Chào buổi sáng, ${name}!`, `Good morning, ${name}!`, `早上好，${name}！`),
      heroLine: L(
        locale,
        'Hôm nay Minh Anh có 1 bài kiểm tra Toán lúc 15:00. Hãy cùng con ôn tập nhé!',
        'Minh Anh has a Math test today at 3:00 PM. Let’s review together!',
        '今天 Minh Anh 下午 3 点有数学测验，一起帮孩子复习吧！',
      ),
      quoteTitle: L(
        locale,
        'Mọi bước tiến nhỏ đều là thành công lớn.',
        'Every small step is a big success.',
        '每一小步都是大成功。',
      ),
      quoteBody: L(
        locale,
        'Sự đồng hành của ba mẹ là nguồn động lực lớn nhất để con tự tin khám phá tri thức.',
        'Your support is the greatest motivation for your child to explore with confidence.',
        '父母的陪伴是孩子自信探索知识的最大动力。',
      ),
      sendNote: L(locale, 'Gửi lời nhắn cho con', 'Send a note to your child', '给孩子留言'),
      yourChildren: L(locale, 'Các con của bạn', 'Your children', '您的孩子'),
      child1Status: L(locale, 'Lớp 5A2 • Đang học', 'Class 5A2 • Studying', '5A2 班 · 学习中'),
      child2Status: L(locale, 'Lớp 3B1 • Đang nghỉ', 'Class 3B1 • On break', '3B1 班 · 休息'),
      recentQuizzes: L(locale, 'Các bài quiz vừa hoàn thành', 'Recently completed quizzes', '最近完成的测验'),
      completed,
    };
  }

  if (r === 'admin') {
    const activityRows = pickRows(
      locale,
      [
        ['Trần Hoàng', 'Đã tạo quiz "Đại số 12"', '2 phút trước', 'Thành công'],
        ['Lê Anh', 'Cập nhật tài liệu ôn tập', '15 phút trước', 'Thành công'],
        ['Minh Tú', 'Xóa quiz lỗi hệ thống', '42 phút trước', 'Đã xóa'],
        ['Khánh Huyền', 'Báo cáo vi phạm nội dung', '1 giờ trước', 'Cần xử lý'],
      ],
      [
        ['Trần Hoàng', 'Created quiz “Algebra 12”', '2 min ago', 'Success'],
        ['Lê Anh', 'Updated study materials', '15 min ago', 'Success'],
        ['Minh Tú', 'Removed faulty quiz', '42 min ago', 'Deleted'],
        ['Khánh Huyền', 'Content violation report', '1 hour ago', 'Needs action'],
      ],
      [
        ['Trần Hoàng', '已创建测验「代数 12」', '2 分钟前', '成功'],
        ['Lê Anh', '已更新学习资料', '15 分钟前', '成功'],
        ['Minh Tú', '已删除故障测验', '42 分钟前', '已删除'],
        ['Khánh Huyền', '内容违规举报', '1 小时前', '待处理'],
      ],
    );
    return {
      heroTitle: L(locale, 'Tổng quan hệ thống', 'System overview', '系统概览'),
      heroSubtitle: L(locale, 'Đây là dashboard dành cho tài khoản quản trị.', 'Admin workspace overview.', '这是管理员账户的工作台。'),
      createQuiz: L(locale, '+ Tạo Quiz mới', '+ Create new quiz', '+ 新建测验'),
      metricUsers: L(locale, 'Tổng số người dùng', 'Total users', '用户总数'),
      metricActive: L(locale, 'Quiz đang hoạt động', 'Active quizzes', '活动测验'),
      metricTraffic: L(locale, 'Lưu lượng truy cập', 'Traffic', '访问量'),
      systemLabel: L(locale, 'Hệ thống', 'System', '系统'),
      systemHealth: L(locale, 'Sức khỏe máy chủ: Tốt', 'Server health: Good', '服务器状态：良好'),
      activityTitle: L(locale, 'Hoạt động giáo viên gần đây', 'Recent teacher activity', '近期教师动态'),
      userSplitTitle: L(locale, 'Phân bổ người dùng', 'User distribution', '用户分布'),
      userStudent: L(locale, 'Học sinh', 'Students', '学生'),
      userTeacher: L(locale, 'Giáo viên', 'Teachers', '教师'),
      userParent: L(locale, 'Phụ huynh', 'Parents', '家长'),
      releaseTitle: L(locale, 'Cập nhật hệ thống', 'System update', '系统更新'),
      releaseBody: L(
        locale,
        'Phiên bản 2.4.0 đang chờ phê duyệt, bao gồm tính năng phòng chờ 3D.',
        'Version 2.4.0 is pending approval, including a 3D waiting room feature.',
        '2.4.0 版本待审批，包含 3D 等候室功能。',
      ),
      ticketTitle: L(locale, 'Ticket reset mật khẩu', 'Password reset tickets', '密码重置工单'),
      ticketSubtitle: L(
        locale,
        'User bấm "reset pass" ở màn login sẽ tạo ticket tại đây.',
        'Tickets appear here when users request a password reset from the login screen.',
        '用户在登录页请求重置密码时，工单会显示在此处。',
      ),
      refresh: L(locale, 'Làm mới', 'Refresh', '刷新'),
      loadingTickets: L(locale, 'Đang tải ticket...', 'Loading tickets…', '正在加载工单…'),
      noTickets: L(locale, 'Hiện chưa có ticket reset password nào.', 'No password reset tickets yet.', '暂无密码重置工单。'),
      ticketUserFallback: L(locale, 'Người dùng', 'User', '用户'),
      ticketProcessing: L(locale, 'Đang xử lý...', 'Processing…', '处理中…'),
      ticketGrantDefault: L(locale, 'Cấp pass mặc định 11111111', 'Set default password 11111111', '设置默认密码 11111111'),
      ticketErrorGeneric: L(locale, 'Không xử lý được ticket.', 'Could not process the ticket.', '无法处理工单。'),
      ticketLoadError: L(locale, 'Không tải được ticket reset password.', 'Could not load reset tickets.', '无法加载重置工单。'),
      formatTicketSuccess: (email, ticketId) =>
        L(
          locale,
          `Đã reset mật khẩu user ${email} về 11111111 và đóng ticket ${ticketId}.`,
          `Password reset for ${email} to 11111111; ticket ${ticketId} closed.`,
          `已将用户 ${email} 的密码重置为 11111111，并关闭工单 ${ticketId}。`,
        ),
      activityRows,
    };
  }

  const demoRows = pickRows(
    locale,
    [
      ['Toán học: Hình học không gian', '15 câu hỏi • 20 phút'],
      ['Tiếng Anh: Grammar Mastery', '30 câu hỏi • 45 phút'],
    ],
    [
      ['Math: Solid geometry', '15 questions • 20 min'],
      ['English: Grammar Mastery', '30 questions • 45 min'],
    ],
    [
      ['数学：立体几何', '15 题 · 20 分钟'],
      ['英语：语法精通', '30 题 · 45 分钟'],
    ],
  );
  return {
    guestName: L(locale, 'Bạn', 'there', '你'),
    heroGreeting: (name) =>
      L(locale, `Chào buổi sáng, ${name}! 👋`, `Hi, ${name}! 👋`, `${name}，你好！👋`),
    heroLine: L(
      locale,
      'Hôm nay bạn muốn chinh phục thử thách nào?',
      'What challenge will you take on today?',
      '今天想挑战什么？',
    ),
    streakLabel: L(locale, 'Chuỗi 7 ngày', '7-day streak', '连续 7 天'),
    streakValue: L(locale, 'Cố lên!', 'Keep it up!', '加油！'),
    urgentTitle: L(locale, 'Bài tập cần làm ngay', 'Due soon', '即将到期的作业'),
    urgentBody: L(
      locale,
      'Toán học và Tiếng Anh đang chờ bạn hoàn thành trong hôm nay.',
      'Math and English are waiting for you to finish today.',
      '今天的数学与英语作业等你完成。',
    ),
    doNow: L(locale, 'Làm ngay', 'Start now', '立即开始'),
    achievementsTitle: L(locale, 'Thành tích đạt được', 'Achievements', '成就'),
    leaderboardTitle: L(locale, 'Bảng xếp hạng lớp', 'Class leaderboard', '班级排行榜'),
    demoRows,
  };
}

export function useDashboardHomeCopy(session) {
  const locale = useDashboardLocaleStore((s) => s.locale);
  const role = session?.role;
  return React.useMemo(() => getDashboardHomeCopy(role, locale), [role, locale]);
}
